// src/services/AccountingService.js
import { collection, query, orderBy, getDocs, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

class AccountingService {
  // Obtener todos los ingresos consolidados
  async getConsolidatedIncome(startDate, endDate) {
    try {
      const income = {
        orders: [],
        eventTickets: [],
        eventPayments: [],
        ampaPayments: [],
        serviceEnrollments: [],
        totalRevenue: 0
      };

      // Obtener ingresos de pedidos
      const ordersQuery = query(
        collection(db, 'orders'),
        where('createdAt', '>=', startDate),
        where('createdAt', '<=', endDate),
        orderBy('createdAt', 'desc')
      );
      const ordersSnapshot = await getDocs(ordersQuery);

      ordersSnapshot.forEach(doc => {
        const order = { id: doc.id, ...doc.data() };
        income.orders.push(order);
        income.totalRevenue += order.total || 0;
      });

      // Obtener tickets de eventos (nueva fuente principal de ingresos de eventos)
      try {
        const eventTicketsQuery = query(
          collection(db, 'eventTickets'),
          where('purchaseDate', '>=', startDate),
          where('purchaseDate', '<=', endDate),
          where('status', '==', 'purchased'),
          orderBy('purchaseDate', 'desc')
        );
        const eventTicketsSnapshot = await getDocs(eventTicketsQuery);

        eventTicketsSnapshot.forEach(doc => {
          const ticket = { id: doc.id, ...doc.data() };
          income.eventTickets.push(ticket);
          income.totalRevenue += ticket.price || 0;
        });
      } catch (error) {
        console.warn('Error fetching eventTickets:', error);
      }

      // Obtener pagos de eventos (colección legacy, si existe)
      try {
        const eventPaymentsQuery = query(
          collection(db, 'eventPayments'),
          where('createdAt', '>=', startDate),
          where('createdAt', '<=', endDate),
          where('status', '==', 'completed'),
          orderBy('createdAt', 'desc')
        );
        const eventPaymentsSnapshot = await getDocs(eventPaymentsQuery);

        eventPaymentsSnapshot.forEach(doc => {
          const payment = { id: doc.id, ...doc.data() };
          income.eventPayments.push(payment);
          income.totalRevenue += payment.amount || 0;
        });
      } catch (error) {
        console.warn('Error fetching eventPayments:', error);
      }

      // Obtener pagos de AMPA (si existe esta colección)
      try {
        const ampaPaymentsQuery = query(
          collection(db, 'ampaPayments'),
          where('createdAt', '>=', startDate),
          where('createdAt', '<=', endDate),
          orderBy('createdAt', 'desc')
        );
        const ampaPaymentsSnapshot = await getDocs(ampaPaymentsQuery);

        ampaPaymentsSnapshot.forEach(doc => {
          const payment = { id: doc.id, ...doc.data() };
          income.ampaPayments.push(payment);
          income.totalRevenue += payment.amount || 0;
        });
      } catch (error) {
        console.warn('Error fetching ampaPayments:', error);
      }

      // Obtener inscripciones a servicios (asumiendo que tienen precio)
      try {
        const serviceEnrollmentsQuery = query(
          collection(db, 'serviceEnrollments'),
          where('createdAt', '>=', startDate),
          where('createdAt', '<=', endDate),
          orderBy('createdAt', 'desc')
        );
        const serviceEnrollmentsSnapshot = await getDocs(serviceEnrollmentsQuery);

        for (const docSnapshot of serviceEnrollmentsSnapshot.docs) {
          const enrollment = { id: docSnapshot.id, ...docSnapshot.data() };
          // Aquí podrías obtener el precio del servicio si está disponible
          income.serviceEnrollments.push(enrollment);
        }
      } catch (error) {
        console.warn('Error fetching serviceEnrollments:', error);
      }

      return income;
    } catch (error) {
      console.error('Error getting consolidated income:', error);
      throw error;
    }
  }

  // Obtener métricas financieras por período
  async getFinancialMetrics(period = 'month') {
    try {
      const now = new Date();
      let startDate, previousStartDate, previousEndDate;

      // Configurar fechas según el período
      switch (period) {
        case 'week':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
          previousStartDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 14);
          previousEndDate = startDate;
          break;
        case 'year':
          startDate = new Date(now.getFullYear(), 0, 1);
          previousStartDate = new Date(now.getFullYear() - 1, 0, 1);
          previousEndDate = new Date(now.getFullYear() - 1, 11, 31);
          break;
        default: // month
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          previousStartDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          previousEndDate = new Date(now.getFullYear(), now.getMonth(), 0);
      }

      const endDate = now;

      // Convertir a Timestamp de Firestore
      const startTimestamp = Timestamp.fromDate(startDate);
      const endTimestamp = Timestamp.fromDate(endDate);
      const previousStartTimestamp = Timestamp.fromDate(previousStartDate);
      const previousEndTimestamp = Timestamp.fromDate(previousEndDate);

      // Obtener datos del período actual
      const currentIncome = await this.getConsolidatedIncome(startTimestamp, endTimestamp);

      // Obtener datos del período anterior
      const previousIncome = await this.getConsolidatedIncome(previousStartTimestamp, previousEndTimestamp);

      // Calcular métricas
      const currentTotal = currentIncome.totalRevenue;
      const previousTotal = previousIncome.totalRevenue;
      const growthRate = previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal) * 100 : 0;

      // Calcular total de pagos de eventos (tickets + eventPayments + ampaPayments)
      const currentEventTicketsTotal = currentIncome.eventTickets.reduce((sum, ticket) => sum + (ticket.price || 0), 0);
      const currentEventPaymentsTotal = currentIncome.eventPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
      const currentAmpaPaymentsTotal = currentIncome.ampaPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
      const currentEventsTotal = currentEventTicketsTotal + currentEventPaymentsTotal + currentAmpaPaymentsTotal;

      const previousEventTicketsTotal = previousIncome.eventTickets.reduce((sum, ticket) => sum + (ticket.price || 0), 0);
      const previousEventPaymentsTotal = previousIncome.eventPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
      const previousAmpaPaymentsTotal = previousIncome.ampaPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);

      // Calcular total de transacciones de eventos
      const currentEventTransactions = currentIncome.eventTickets.length + currentIncome.eventPayments.length + currentIncome.ampaPayments.length;
      const previousEventTransactions = previousIncome.eventTickets.length + previousIncome.eventPayments.length + previousIncome.ampaPayments.length;

      return {
        currentPeriod: {
          totalRevenue: currentTotal,
          orderCount: currentIncome.orders.length,
          eventPaymentCount: currentEventTransactions,
          eventTicketsCount: currentIncome.eventTickets.length,
          serviceEnrollmentCount: currentIncome.serviceEnrollments.length,
          averageOrderValue: currentIncome.orders.length > 0
            ? currentIncome.orders.reduce((sum, order) => sum + (order.total || 0), 0) / currentIncome.orders.length
            : 0
        },
        previousPeriod: {
          totalRevenue: previousTotal,
          orderCount: previousIncome.orders.length,
          eventPaymentCount: previousEventTransactions,
          eventTicketsCount: previousIncome.eventTickets.length,
          serviceEnrollmentCount: previousIncome.serviceEnrollments.length
        },
        comparison: {
          growthRate,
          revenueChange: currentTotal - previousTotal,
          isGrowth: currentTotal >= previousTotal
        },
        breakdown: {
          products: currentIncome.orders.reduce((sum, order) => sum + (order.total || 0), 0),
          events: currentEventTicketsTotal + currentEventPaymentsTotal,
          ampaFees: currentAmpaPaymentsTotal,
          services: 0 // Se puede calcular si los servicios tienen precio
        }
      };
    } catch (error) {
      console.error('Error getting financial metrics:', error);
      throw error;
    }
  }

  // Obtener datos para gráficos por meses
  async getMonthlyData(year = new Date().getFullYear()) {
    try {
      const monthlyData = Array.from({ length: 12 }, (_, index) => ({
        month: index + 1,
        monthName: new Date(year, index).toLocaleDateString('es-ES', { month: 'long' }),
        orders: 0,
        events: 0,
        ampaFees: 0,
        total: 0
      }));

      // Obtener todos los datos del año
      const startOfYear = Timestamp.fromDate(new Date(year, 0, 1));
      const endOfYear = Timestamp.fromDate(new Date(year, 11, 31, 23, 59, 59));

      const yearlyIncome = await this.getConsolidatedIncome(startOfYear, endOfYear);

      // Procesar pedidos
      yearlyIncome.orders.forEach(order => {
        if (order.createdAt) {
          const orderDate = order.createdAt instanceof Timestamp
            ? order.createdAt.toDate()
            : new Date(order.createdAt.seconds * 1000);
          const monthIndex = orderDate.getMonth();
          monthlyData[monthIndex].orders += order.total || 0;
          monthlyData[monthIndex].total += order.total || 0;
        }
      });

      // Procesar tickets de eventos
      yearlyIncome.eventTickets.forEach(ticket => {
        if (ticket.purchaseDate) {
          const ticketDate = ticket.purchaseDate instanceof Timestamp
            ? ticket.purchaseDate.toDate()
            : new Date(ticket.purchaseDate.seconds * 1000);
          const monthIndex = ticketDate.getMonth();
          monthlyData[monthIndex].events += ticket.price || 0;
          monthlyData[monthIndex].total += ticket.price || 0;
        }
      });

      // Procesar pagos de eventos (legacy)
      yearlyIncome.eventPayments.forEach(payment => {
        if (payment.createdAt) {
          const paymentDate = payment.createdAt instanceof Timestamp
            ? payment.createdAt.toDate()
            : new Date(payment.createdAt.seconds * 1000);
          const monthIndex = paymentDate.getMonth();
          monthlyData[monthIndex].events += payment.amount || 0;
          monthlyData[monthIndex].total += payment.amount || 0;
        }
      });

      // Procesar pagos de AMPA
      yearlyIncome.ampaPayments.forEach(payment => {
        if (payment.createdAt) {
          const paymentDate = payment.createdAt instanceof Timestamp
            ? payment.createdAt.toDate()
            : new Date(payment.createdAt.seconds * 1000);
          const monthIndex = paymentDate.getMonth();
          monthlyData[monthIndex].ampaFees += payment.amount || 0;
          monthlyData[monthIndex].total += payment.amount || 0;
        }
      });

      return monthlyData;
    } catch (error) {
      console.error('Error getting monthly data:', error);
      throw error;
    }
  }

  // Obtener top elementos por ingresos
  async getTopItems(type = 'products', limit = 5) {
    try {
      const topItems = [];

      if (type === 'products') {
        // Analizar productos más vendidos en pedidos
        const ordersQuery = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
        const ordersSnapshot = await getDocs(ordersQuery);

        const productSales = {};

        ordersSnapshot.forEach(doc => {
          const order = doc.data();
          if (order.items && Array.isArray(order.items)) {
            order.items.forEach(item => {
              const productKey = item.nombre || 'Producto sin nombre';
              if (!productSales[productKey]) {
                productSales[productKey] = {
                  name: productKey,
                  quantity: 0,
                  revenue: 0
                };
              }
              productSales[productKey].quantity += item.quantity || 0;
              productSales[productKey].revenue += (item.precio || 0) * (item.quantity || 0);
            });
          }
        });

        return Object.values(productSales)
          .sort((a, b) => b.revenue - a.revenue)
          .slice(0, limit);
      }

      return topItems;
    } catch (error) {
      console.error('Error getting top items:', error);
      throw error;
    }
  }

  // Obtener resumen rápido para dashboard
  async getDashboardSummary() {
    try {
      const now = new Date();
      const startOfMonth = Timestamp.fromDate(new Date(now.getFullYear(), now.getMonth(), 1));
      const endOfMonth = Timestamp.fromDate(now);

      const monthlyIncome = await this.getConsolidatedIncome(startOfMonth, endOfMonth);
      const metrics = await this.getFinancialMetrics('month');

      return {
        monthlyRevenue: monthlyIncome.totalRevenue,
        transactionCount: monthlyIncome.orders.length + monthlyIncome.eventPayments.length + monthlyIncome.eventTickets.length + monthlyIncome.ampaPayments.length + monthlyIncome.serviceEnrollments.length,
        growthRate: metrics.comparison.growthRate,
        breakdown: metrics.breakdown
      };
    } catch (error) {
      console.error('Error getting dashboard summary:', error);
      return {
        monthlyRevenue: 0,
        transactionCount: 0,
        growthRate: 0,
        breakdown: { products: 0, events: 0, ampaFees: 0, services: 0 }
      };
    }
  }
}

export default new AccountingService();